import React, { useState, useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { useTheme } from "../context/ThemeContext";

// --- const for Parallax ---
const PARALLAX_RATE = 0.8;
const IMAGE_HEIGHT = 2413;

// начальное смещение фона вверх
const INITIAL_Y_SHIFT = -350;

const ParallaxBackground = styled.div`
  width: 100%;
  position: relative;
  overflow: hidden;
  min-height: 100vh;
`;

const ParallaxImage = styled.div.attrs((props) => ({
  style: {
    // Применяем смещение
    transform: `translateY(${props.$yOffset}px)`,
    height: `${IMAGE_HEIGHT}px`,
  },
}))`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1;
  pointer-events: none;

  background-image: url("./images/skillsBackActive.png");

  background-size: 876px ${IMAGE_HEIGHT}px;
  background-position: center top;
  background-repeat: no-repeat;

  will-change: transform;

  ${(props) =>
    props.$isDark &&
    css`
      filter: invert(1);
      opacity: 0.7;
    `}
  ${(props) =>
    !props.$isDark &&
    css`
      opacity: 0.7;
    `}
`;

const Section = styled.section`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;

  position: relative;
  z-index: 10;

  background-color: transparent;
  overflow: hidden;

  padding: 6rem 1.5rem;
  font-family:
    "Trebuchet MS", "Lucida Sans Unicode", "Lucida Grande", "Lucida Sans",
    Arial, sans-serif;

  @media (max-width: 640px) {
    padding: 6rem 0.8rem;
  }
`;

const ContentWrapper = styled.div`
  max-width: 40rem;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 10;
  width: 100%;
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  font-family: Philosopher;
  margin-bottom: 2.5rem;
  text-align: center;
  color: var(--color-text);
`;

const Card = styled.div`
  background-color: rgba(255, 255, 255, 0.14); /* 1. Полупрозрачный белый фон */
  backdrop-filter: blur(
    2px
  ); /* 2. Размытие всего, что находится ПОД элементом */
  -webkit-backdrop-filter: blur(
    2px
  ); /* 3. Префикс для старых версий Chrome/Safari */

  border-radius: 0.75rem;
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.1);
  padding: 3rem;
  transition: all 150ms ease-in-out;
  cursor: default;

  body.dark-theme & {
    background-color: rgba(41, 40, 40, 0.43);
    box-shadow:
      0 4px 6px -1px rgba(255, 255, 255, 0.05),
      0 2px 4px -2px rgba(255, 255, 255, 0.05);
  }
  @media (max-width: 640px) {
    padding: 15px;
  }
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  & > *:not(:last-child) {
    margin-bottom: 0.75rem;
  }
`;

const ListItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f3f4f6;

  body.dark-theme & {
    border-bottom: 1px solid #374151;
  }
  margin-bottom: 0.5rem !important;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0 !important;
  }
  body.dark-theme &:last-child {
    border-bottom: none; /* Убирает рамку в темной теме */
  }
`;

const ListItemContent = styled.span`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 500;
  color: var(--color-text);
  & > img {
    width: 32px;
    height: 32px;
    object-fit: contain;
    filter: none !important;
  }
`;

const LevelText = styled.span`
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #6b7280;
  body.dark-theme & {
    color: #9ca3af;
  }
  font-style: italic;
`;

export default function Skills() {
  const { theme } = useTheme();
  const isDark = theme === "dark";

  // Устанавливаем начальное смещение!
  const [offsetY, setOffsetY] = useState(INITIAL_Y_SHIFT);
  const componentRef = useRef(null);

  const handleScroll = () => {
    if (!componentRef.current) return;

    const scrollPosition = window.scrollY;
    const rect = componentRef.current.getBoundingClientRect();

    const componentTop = rect.top + scrollPosition;
    const scrollIntoComponent = scrollPosition - componentTop;

    // Рассчитываем смещение с учетом начального сдвига
    // Мы просто добавляем INITIAL_Y_SHIFT к расчету
    const dynamicOffset = scrollIntoComponent * PARALLAX_RATE * -1;
    const newOffset = dynamicOffset + INITIAL_Y_SHIFT;
    setOffsetY(newOffset);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const allSkills = [
    { name: "HTML", icon: "./icons/html.png", level: "Экспертное владение" },
    { name: "CSS", icon: "./icons/css.png", level: "Экспертное владение" },
    { name: "SASS", icon: "./icons/sass.png", level: "Свободное владение" },
    { name: "JavaScript", icon: "./icons/js.png", level: "Уверенное владение" },
    { name: "React", icon: "./icons/react.png", level: "Базовое понимание" },
    {
      name: "Styled Components",
      icon: "./icons/styled.png",
      level: "Базовое понимание",
    },
    {
      name: "Git / GitHub",
      icon: "./icons/git.png",
      level: "Базовое понимание",
    },
    // { name: "VS Code", icon: "./icons/vscode.png", level: "Базовое понимание" },
  ];

  return (
    <ParallaxBackground ref={componentRef}>
      <ParallaxImage $isDark={isDark} $yOffset={offsetY} />

      <Section id="skills">
        <ContentWrapper>
          <Title>Навыки</Title>

          <Card>
            <List>
              {allSkills.map((skill) => {
                {
                  /* Получаем базовое имя (например, "./icons/html") */
                }
                const baseIconPath = skill.icon.replace(".png", "");
                {
                  /* Добавляем "-dark" или "-light" в зависимости от темы */
                }
                const dynamicIconPath = isDark
                  ? `${baseIconPath}-d.png`
                  : `${baseIconPath}.png`;
                // -------------------------------------------
                return (
                  <ListItem key={skill.name}>
                    <ListItemContent>
                      <img src={dynamicIconPath} alt={`${skill.name} icon`} />
                      {skill.name}
                    </ListItemContent>
                    <LevelText>{skill.level}</LevelText>
                  </ListItem>
                );
              })}
            </List>
          </Card>
        </ContentWrapper>
      </Section>
    </ParallaxBackground>
  );
}
